import React, { createContext, useContext, useState, useRef } from 'react';
import firebase from 'firebase/compat/app';
import db_config from '../../configs/db_config';
import { db } from '../../libs/firebase';
import { DownloadTicketOrder, Order, DownloadTicketRestaurant } from '../../interfaces/types';

interface SeatInfo {
  id: string;
  area?: string;
  seat_type?: string;
  block?: string;
  row?: string;
  seat_number?: string;
  [key: string]: unknown;
}

interface DownloadTicketContextType {
  order: DownloadTicketOrder | null;
  loading: boolean;
  fetchOrderById: (orderId: string) => Promise<DownloadTicketOrder | null>;
  fetchSeatById?: (seatId: string, stadiumId?: string) => Promise<SeatInfo | null>;
  getFormattedSeatById?: (seatId?: string, stadiumId?: string) => Promise<string>;
}

const DownloadTicketContext = createContext<DownloadTicketContextType>({
  order: null,
  loading: false,
  fetchOrderById: async () => null,
});

export const useDownloadTicketContext = () => useContext(DownloadTicketContext);

export const DownloadTicketProvider: React.FC = ({ children }) => {
  const [order, setOrder] = useState<DownloadTicketOrder | null>(null);
  const [loading, setLoading] = useState(false);
  const restaurantCache = useRef<Record<string, DownloadTicketRestaurant | null>>({});

  const fetchSeatById = async (seatId: string, stadiumId?: string): Promise<SeatInfo | null> => {
    if (!seatId) return null;
    const sid = stadiumId || order?.stadium_id;
    if (!sid) return null;
    try {
      const seatRef = db.collection(db_config.DB_TABLE_STADIUM_MANAGE_SEATS).doc(sid).collection('data_2026').doc(seatId);
      const seatDoc = await seatRef.get();
      if (!seatDoc.exists) return null;
      return { id: seatDoc.id, ...(seatDoc.data() || {}) } as SeatInfo;
    } catch (err) {
      return null;
    }
  };

  const getFormattedSeatById = async (seatId?: string, stadiumId?: string): Promise<string> => {
    if (!seatId) return '';
    const seat = await fetchSeatById(seatId, stadiumId);
    if (!seat) return '';
    const parts = [seat.area, seat.seat_type, seat.block, seat.row, seat.seat_number];
    return parts.filter(Boolean).join('・');
  };

  const fetchOrderById = async (orderId: string) => {
    setLoading(true);
    try {
      let orderDoc;
      try {
        orderDoc = await db.collection(db_config.DB_TABLE_ALL_ORDERS).doc(orderId).get();
      } catch (err) {
        setLoading(false);
        return null;
      }
      if (!orderDoc.exists) {
        setLoading(false);
        return null;
      }

      const orderData = orderDoc.data() as Order;

      const fetchStadium = async (stadiumId?: string) => {
        if (!stadiumId) return null;
        try {
          const stadiumDoc = await db.collection(db_config.DB_TABLE_STADIUM_MANAGE).doc(stadiumId).get();
          return stadiumDoc.exists ? stadiumDoc.data() : null;
        } catch (err) {
          return null;
        }
      };

      const fetchAdminRestaurants = async () => {
        try {
          const list: DownloadTicketRestaurant[] = [];
          const snaps = await db.collection(db_config.DB_TABLE_RESTAURANT_CONFIG).where('is_admin_store', '==', true).get();
          snaps.forEach((d) => {
            const data = d.data();
            const r = {
              id: d.id,
              name: data?.name ?? '',
              restaurant_profile_pic: data?.restaurant_profile_pic ?? '',
              is_admin_store: data?.is_admin_store ?? false,
              restaurant_operators: data?.restaurant_operators ?? []
            } as DownloadTicketRestaurant;
            list.push(r);
          });
          return list;
        } catch (err) {
          return [] as DownloadTicketRestaurant[];
        }
      };

      type FoodRef = { restaurantId?: string; restaurant_id?: string };
      type FoodWithAltId = import('../../interfaces/types').FoodItem & { restaurant_id?: string };
      const collectRestaurantIdsFromFoods = (o: Order): string[] => {
        if (!Array.isArray(o.foods) && !Array.isArray(o.promotion_foods)) return [];
        const fromFoods = [...(o.foods ?? []), ...(o.promotion_foods ?? [])];
        const ids = fromFoods.map((f: FoodRef) => f.restaurantId ?? f.restaurant_id).filter(Boolean) as string[];
        return Array.from(new Set(ids));
      };

      const fetchRestaurantsByIds = async (ids: string[]) => {
        if (!ids.length) return { list: [] as DownloadTicketRestaurant[], map: {} as Record<string, DownloadTicketRestaurant | null> };
        const map: Record<string, DownloadTicketRestaurant | null> = {};
        const list: DownloadTicketRestaurant[] = [];
        const chunkSize = 10;

        for (let i = 0; i < ids.length; i += chunkSize) {
          const chunk = ids.slice(i, i + chunkSize);
          try {
            const snaps = await db.collection(db_config.DB_TABLE_RESTAURANT_CONFIG).where(firebase.firestore.FieldPath.documentId(), 'in', chunk).get();
            snaps.forEach((d) => {
              const data = d.data();
              const r = { 
                id: d.id, 
                name: data?.name ?? '', 
                restaurant_profile_pic: data?.restaurant_profile_pic ?? '',
                is_admin_store: data?.is_admin_store ?? false,
                restaurant_operators: data?.restaurant_operators ?? []
              } as DownloadTicketRestaurant;
              map[d.id] = r;
              list.push(r);
            });
            chunk.forEach((id) => {
              if (!(id in map)) map[id] = null;
            });
          } catch (err) {
            await Promise.all(
              chunk.map(async (rid) => {
                try {
                  const d = await db.collection(db_config.DB_TABLE_RESTAURANT_CONFIG).doc(rid).get();
                  if (!d.exists) {
                    map[rid] = null;
                    return;
                  }
                  const data = d.data();
                  const r = { 
                    id: rid, 
                    name: data?.name ?? '', 
                    restaurant_profile_pic: data?.restaurant_profile_pic ?? '',
                    is_admin_store: data?.is_admin_store ?? false,
                    restaurant_operators: data?.restaurant_operators ?? []
                  } as DownloadTicketRestaurant;
                  map[rid] = r;
                  list.push(r);
                } catch (e) {
                  map[rid] = null;
                }
              })
            );
          }
        }

        return { list, map };
      };

      const restaurantIds = collectRestaurantIdsFromFoods(orderData);
      const [finalStadium, finalRestaurantResult, adminRestaurants] = await Promise.all([
        fetchStadium(orderData.stadium_id),
        fetchRestaurantsByIds(restaurantIds),
        fetchAdminRestaurants(),
      ]);
      const finalRestaurant = finalRestaurantResult.list.length ? finalRestaurantResult.list : null;

      const adminRestaurant = adminRestaurants.length ? adminRestaurants[0] : finalRestaurantResult.list.find((r: DownloadTicketRestaurant) => r.is_admin_store === true);
      const restaurantName = adminRestaurant?.name ?? finalStadium?.name ?? '';
      const emailAdmin = adminRestaurant?.restaurant_operators?.[0]?.email ?? '';

      const enrichFood = (f: FoodWithAltId) => {
        const rid = f.restaurantId ?? f.restaurant_id ?? '';
        const r = rid ? finalRestaurantResult.map[rid] : undefined;
        return {
          ...f,
          restaurant_name: r?.name ?? undefined,
          restaurant_profile_pic: r?.restaurant_profile_pic ?? undefined,
        };
      };

      const merged: DownloadTicketOrder = {
        ...orderData,
        stadium_profile_pic: finalStadium?.stadium_profile_pic ?? '',
        registration_code: finalStadium?.registration_code ?? '',
        business_name: finalStadium?.business_name ?? '',
        address: finalStadium?.address ?? '',
        name: restaurantName,
        rider_contact: finalStadium?.rider_contact ?? '',
        stadium_phone: finalStadium?.phone ?? '',
        email: finalStadium?.email ?? '',
        email_admin: emailAdmin,
        foods: Array.isArray(orderData.foods) ? orderData.foods.map(enrichFood) : orderData.foods,
        promotion_foods: Array.isArray(orderData.promotion_foods) ? orderData.promotion_foods.map(enrichFood) : orderData.promotion_foods,
        restaurant: finalRestaurant ? (Array.isArray(finalRestaurant) ? finalRestaurant : [finalRestaurant]) : undefined,
      };

      setOrder(merged);
      setLoading(false);
      return merged;
    } catch (e) {
      setLoading(false);
      return null;
    }
  };

  return <DownloadTicketContext.Provider value={{ order, loading, fetchOrderById, fetchSeatById, getFormattedSeatById }}>{children}</DownloadTicketContext.Provider>;
};

export default DownloadTicketContext;
